import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded'
import { Box, Card, Typography } from '@mui/material'

import { clickableProps } from '../../helpers/interactive'
import { motivationColor } from '../../helpers/motivationColors'
import { formatDelta } from '../../helpers/scoreDisplay'
import { clampPct, deltaColor } from '../../helpers/showcase'
import { focusRingSx } from '../../helpers/styles'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { ShowcaseMotivation } from '../../types'

interface CreativeMotivationsChartProps {
  motivations: ShowcaseMotivation[]
  onSelect: (motivationId: string) => void
  /** Render without the outlined card + inner title (e.g. inside a `SectionCard`). */
  disableCard?: boolean
}

/** Bar-fill opacity tier by motivation prevalence (mirrors the prototype's high/mid/low). */
const barOpacity = (pct: number): number => (pct >= 15 ? 1 : pct >= 6 ? 0.65 : 0.4)

type SortKey = 'name' | 'share' | 'change' | 'ads'
type SortDirection = 'asc' | 'desc'

/**
 * Shared column geometry so the header row and data rows stay aligned.
 *
 * The two trailing columns are sized for their *headers*, not their values —
 * the values are never more than a few characters, while the labels run to
 * eight full-width glyphs in Japanese ("クリエイティブ数") plus the sort arrow.
 * Sizing them any tighter wraps the label mid-word, which reads badly in CJK.
 */
const COLUMN = {
  rank: 18,
  name: 180,
  pct: 44,
  delta: 78,
  ads: 108,
} as const

/**
 * Ranked motivation bars for the overview. Sortable column headers reorder the
 * rows in place; each row navigates to that motivation's detail view, and a
 * hover cue hints at the drill-down.
 */
export const CreativeMotivationsChart: FC<CreativeMotivationsChartProps> = ({ motivations, onSelect, disableCard = false }) => {
  const { t } = useTranslation('ua-insights')
  const { motivationLabel } = useUATaxonomy()

  // Default to the most prevalent motivations first, matching the ranked view.
  const [sortKey, setSortKey] = useState<SortKey>('share')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    // Names read most naturally A→Z; metrics most naturally highest-first.
    setSortDirection(key === 'name' ? 'asc' : 'desc')
  }

  const sortedMotivations = useMemo(() => {
    const factor = sortDirection === 'asc' ? 1 : -1
    return [...motivations].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          // Sort on the rendered label, otherwise a translated list appears
          // to sort at random (it would be ordered by the hidden English name).
          return motivationLabel(a.motivation).localeCompare(motivationLabel(b.motivation)) * factor
        case 'change':
          return ((a.deltaPp ?? 0) - (b.deltaPp ?? 0)) * factor
        case 'ads':
          return (a.creativesCount - b.creativesCount) * factor
        case 'share':
        default:
          return (a.sharePct - b.sharePct) * factor
      }
    })
  }, [motivations, sortKey, sortDirection, motivationLabel])

  // The rank shown in the "#" column is fixed to the Share of Creatives order
  // (highest share = #1) and does not follow the active column sort, so a
  // motivation keeps the same number however the rows are reordered.
  const shareRankById = useMemo(() => {
    const byShare = [...motivations].sort((a, b) => b.sharePct - a.sharePct)
    return new Map(byShare.map((row, index) => [row.motivation.id, index + 1]))
  }, [motivations])

  // Inactive columns keep the arrow's slot reserved instead of dropping it, so
  // sorting a column doesn't widen its header and reflow the row.
  const SortIndicator: FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    const active = columnKey === sortKey
    const Icon = active && sortDirection === 'asc' ? ArrowUpwardRounded : ArrowDownwardRounded
    return <Icon sx={{ fontSize: '0.875rem', flexShrink: 0, visibility: active ? 'visible' : 'hidden' }} />
  }

  const SortableHeader: FC<{ columnKey: SortKey; label: string; align?: 'left' | 'right'; sx?: object }> = ({ columnKey, label, align = 'left', sx }) => {
    const active = columnKey === sortKey
    return (
      <Box
        component="button"
        type="button"
        onClick={() => handleSort(columnKey)}
        sx={{
          background: 'none',
          border: 'none',
          p: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          textAlign: align,
          fontSize: '0.6875rem',
          fontWeight: 700,
          lineHeight: 1.25,
          color: active ? 'text.primary' : 'text.secondary',
          '&:hover': { color: 'text.primary' },
          ...sx,
        }}
      >
        {align === 'right' && <SortIndicator columnKey={columnKey} />}
        {/* Columns are sized to fit every translation, but a label that still
            outgrows its column wraps rather than overflowing — an overflowing
            label lands on top of the neighbouring header. */}
        <Box component="span" sx={{ minWidth: 0 }}>
          {label}
        </Box>
        {align === 'left' && <SortIndicator columnKey={columnKey} />}
      </Box>
    )
  }

  const title = disableCard ? (
    // Inside a SectionCard the bold title is the card header, so only the hint
    // is repeated here as a lightweight caption above the bars.
    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 'normal', color: 'text.secondary', mb: 1.5 }}>{t('showcase_motivation_landscape_hint')}</Typography>
  ) : (
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {t('showcase_motivation_landscape')}
      <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 'normal', color: 'text.secondary' }}>
        {t('showcase_motivation_landscape_hint')}
      </Box>
    </Typography>
  )

  const columnHeaders = (
    <Box
      sx={{
        display: 'flex',
        // Bottom-aligned so a label that wraps to two lines keeps its last
        // line sitting on the divider alongside the single-line headers.
        alignItems: 'flex-end',
        gap: 1.25,
        px: 1,
        mx: -1,
        pb: 0.75,
        mb: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ width: COLUMN.rank, textAlign: 'center', flexShrink: 0 }}>
        <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary' }}>
          {t('showcase_col_rank')}
        </Typography>
      </Box>
      <SortableHeader columnKey="name" label={t('showcase_col_motivation')} sx={{ width: COLUMN.name, flexShrink: 0 }} />
      <SortableHeader columnKey="share" label={t('showcase_col_share')} sx={{ flex: 1, minWidth: 40 }} />
      <Box sx={{ width: COLUMN.pct, flexShrink: 0 }} />
      <SortableHeader columnKey="change" label={t('showcase_col_change')} align="right" sx={{ width: COLUMN.delta, flexShrink: 0 }} />
      <SortableHeader
        columnKey="ads"
        label={t('showcase_col_num_creatives')}
        align="right"
        sx={{ width: COLUMN.ads, flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
      />
    </Box>
  )

  const body = (
    <>
      {title}
      {columnHeaders}
      <Box>
        {sortedMotivations.map((row) => (
          <Box
            key={row.motivation.id}
            {...clickableProps(() => onSelect(row.motivation.id))}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1,
              py: 0.625,
              mx: -1,
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'background-color 0.1s ease',
              '&:hover': { bgcolor: 'action.hover' },
              ...focusRingSx(-2),
            }}
          >
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', width: COLUMN.rank, textAlign: 'center', flexShrink: 0 }}>
              {shareRankById.get(row.motivation.id)}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', width: COLUMN.name, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {motivationLabel(row.motivation)}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 40, height: 14, bgcolor: 'action.hover', borderRadius: '999px', overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${clampPct(row.sharePct)}%`,
                  bgcolor: motivationColor(row.motivation),
                  opacity: barOpacity(row.sharePct),
                  borderRadius: '999px',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, width: COLUMN.pct, textAlign: 'right', flexShrink: 0 }}>{`${Math.round(
              row.sharePct
            )}%`}</Typography>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, width: COLUMN.delta, textAlign: 'right', flexShrink: 0, color: deltaColor(row.deltaPp) }}>
              {formatDelta(row.deltaPp)}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: 'text.secondary',
                width: COLUMN.ads,
                textAlign: 'right',
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {row.creativesCount}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  )

  if (disableCard) return body

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      {body}
    </Card>
  )
}
